/**
 * Insight Archetypes Service
 *
 * Handles fetching and persisting Explorer-level insight selections.
 * Uses reusable civic lens archetypes matched to songs.
 */

import { supabase } from './supabaseClient';

export interface InsightArchetype {
  id: string;
  insight_text: string;
  category: string;
  theme_tags: string[];
}

export interface SongInsightMatch {
  id: string;
  song_id: string;
  archetype_id: string;
  display_order: number;
  anchor_text: string | null;
  why_it_matters: string | null;
  archetype: InsightArchetype;
}

export interface UserInsightSelection {
  id: string;
  user_id: string;
  song_id: string;
  archetype_id: string;
  selected_at: string;
}

/**
 * Fetch the 3 matched insights for a song (returns full matches with anchor text)
 * GUARANTEED to return exactly 3 results with category diversity
 */
export async function getSongInsights(songId: string): Promise<SongInsightMatch[]> {
  try {
    const { data, error } = await supabase
      .from('song_insight_matches')
      .select(`
        id,
        song_id,
        archetype_id,
        display_order,
        anchor_text,
        why_it_matters,
        archetype:insight_archetypes (
          id,
          insight_text,
          category,
          theme_tags
        )
      `)
      .eq('song_id', songId)
      .order('display_order');

    if (error) {
      console.error('[getSongInsights] Error:', error);
      return await backfillInsights(songId);
    }

    const validMatches = (data || []).filter((match: any) => match.archetype !== null) as SongInsightMatch[];

    if (validMatches.length === 0) {
      console.log('[getSongInsights] No insights found, generating...');
      await generateInsightsForSong(songId);

      const { data: retryData, error: retryError } = await supabase
        .from('song_insight_matches')
        .select(`
          id,
          song_id,
          archetype_id,
          display_order,
          anchor_text,
          why_it_matters,
          archetype:insight_archetypes (
            id,
            insight_text,
            category,
            theme_tags
          )
        `)
        .eq('song_id', songId)
        .order('display_order');

      if (retryError || !retryData || retryData.length === 0) {
        console.error('[getSongInsights] Retry failed, backfilling:', retryError);
        return await backfillInsights(songId);
      }

      const retryValidMatches = retryData.filter((match: any) => match.archetype !== null) as SongInsightMatch[];

      if (retryValidMatches.length < 3) {
        return await backfillInsights(songId, retryValidMatches);
      }

      return retryValidMatches.slice(0, 3);
    }

    if (validMatches.length < 3) {
      console.log(`[getSongInsights] Only ${validMatches.length} insights, backfilling to 3...`);
      return await backfillInsights(songId, validMatches);
    }

    return validMatches.slice(0, 3);
  } catch (err) {
    console.error('[getSongInsights] Unexpected error:', err);
    return await backfillInsights(songId);
  }
}

/**
 * Backfill insights to guarantee exactly 3 with category diversity
 */
async function backfillInsights(
  songId: string,
  existingMatches: SongInsightMatch[] = []
): Promise<SongInsightMatch[]> {
  try {
    const neededCount = 3 - existingMatches.length;
    if (neededCount <= 0) return existingMatches.slice(0, 3);

    const usedCategories = existingMatches.map(m => m.archetype.category);
    const usedArchetypeIds = existingMatches.map(m => m.archetype_id);

    const { data: songData } = await supabase
      .from('songs')
      .select('tags, draft_secondary_tags')
      .eq('id', songId)
      .maybeSingle();

    const songTags = [
      ...(songData?.tags || []),
      ...(songData?.draft_secondary_tags || []),
    ];

    const { data: candidates } = await supabase
      .from('insight_archetypes')
      .select('*')
      .eq('enabled', true)
      .limit(30);

    if (!candidates || candidates.length === 0) {
      return existingMatches;
    }

    const availableCandidates = candidates.filter(
      arch => !usedArchetypeIds.includes(arch.id)
    );

    const scored = availableCandidates.map(arch => {
      const tagMatches = arch.theme_tags.filter((tag: string) =>
        songTags.includes(tag)
      ).length;
      const categoryBonus = !usedCategories.includes(arch.category) ? 5 : 0;
      return {
        archetype: arch,
        score: tagMatches + categoryBonus + Math.random() * 0.5,
      };
    });

    scored.sort((a, b) => b.score - a.score);
    const selected = scored.slice(0, neededCount);

    const backfilled: SongInsightMatch[] = selected.map((s, idx) => ({
      id: `backfill-${songId}-${idx}`,
      song_id: songId,
      archetype_id: s.archetype.id,
      display_order: existingMatches.length + idx + 1,
      anchor_text: null,
      why_it_matters: `This lens helps you see how ${s.archetype.category} shows up in the song's story.`,
      archetype: {
        id: s.archetype.id,
        insight_text: s.archetype.insight_text,
        category: s.archetype.category,
        theme_tags: s.archetype.theme_tags,
      },
    }));

    return [...existingMatches, ...backfilled].slice(0, 3);
  } catch (err) {
    console.error('[backfillInsights] Error:', err);
    return existingMatches;
  }
}

/**
 * Generate insights for a song (calls database function)
 */
async function generateInsightsForSong(songId: string): Promise<void> {
  try {
    const { error } = await supabase.rpc('generate_song_insights', {
      p_song_id: songId,
    });

    if (error) {
      console.error('[generateInsightsForSong] Error:', error);
    }
  } catch (err) {
    console.error('[generateInsightsForSong] Unexpected error:', err);
  }
}

/**
 * Get user's selected insight for a song (if any)
 */
export async function getUserSelectedInsight(
  userId: string,
  songId: string
): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('user_selected_insights')
      .select('archetype_id')
      .eq('user_id', userId)
      .eq('song_id', songId)
      .maybeSingle();

    if (error) {
      console.error('[getUserSelectedInsight] Error:', error);
      return null;
    }

    return data?.archetype_id || null;
  } catch (err) {
    console.error('[getUserSelectedInsight] Unexpected error:', err);
    return null;
  }
}

/**
 * Save user's insight selection (upsert)
 */
export async function saveUserInsightSelection(
  userId: string,
  songId: string,
  archetypeId: string
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_selected_insights')
      .upsert(
        {
          user_id: userId,
          song_id: songId,
          archetype_id: archetypeId,
          selected_at: new Date().toISOString(),
        },
        {
          onConflict: 'user_id,song_id',
        }
      );

    if (error) {
      console.error('[saveUserInsightSelection] Error:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('[saveUserInsightSelection] Unexpected error:', err);
    return false;
  }
}

/**
 * Re-roll a single insight match while maintaining category diversity
 * Replaces the match at the given display order with a new archetype
 */
export async function rerollSingleInsight(
  songId: string,
  displayOrder: number
): Promise<SongInsightMatch | null> {
  try {
    // Get current matches to preserve diversity
    const currentMatches = await getSongInsights(songId);
    if (currentMatches.length === 0) {
      return null;
    }

    // Get categories of other matches (not the one being re-rolled)
    const otherCategories = currentMatches
      .filter(m => m.display_order !== displayOrder)
      .map(m => m.archetype.category);

    // Get song tags for matching
    const { data: songData } = await supabase
      .from('songs')
      .select('tags, draft_secondary_tags')
      .eq('id', songId)
      .single();

    const songTags = [
      ...(songData?.tags || []),
      ...(songData?.draft_secondary_tags || []),
    ];

    // Find a new archetype that:
    // 1. Isn't already used in this song
    // 2. Preferably from a different category than other matches
    // 3. Has tag overlap with the song
    const usedArchetypeIds = currentMatches.map(m => m.archetype.id);

    const { data: candidates, error } = await supabase
      .from('insight_archetypes')
      .select('*')
      .eq('enabled', true)
      .not('id', 'in', `(${usedArchetypeIds.join(',')})`)
      .limit(20);

    if (error || !candidates || candidates.length === 0) {
      console.error('[rerollSingleInsight] No candidates found:', error);
      return null;
    }

    // Score candidates: prefer different category and tag overlap
    const scored = candidates.map(arch => {
      const tagMatches = arch.theme_tags.filter((tag: string) =>
        songTags.includes(tag)
      ).length;
      const categoryPenalty = otherCategories.includes(arch.category) ? -10 : 0;
      return {
        archetype: arch,
        score: tagMatches + categoryPenalty + Math.random(),
      };
    });

    scored.sort((a, b) => b.score - a.score);
    const newArchetype = scored[0].archetype;

    // Generate anchor text and why it matters
    const { data: anchorData } = await supabase.rpc(
      'generate_anchor_text_for_match',
      {
        p_song_id: songId,
        p_archetype_category: newArchetype.category,
        p_archetype_themes: newArchetype.theme_tags,
      }
    );

    const { data: whyData } = await supabase.rpc(
      'generate_why_it_matters_for_match',
      {
        p_archetype_category: newArchetype.category,
        p_archetype_themes: newArchetype.theme_tags,
        p_song_tags: songTags,
      }
    );

    // Update the match
    const { data: updated, error: updateError } = await supabase
      .from('song_insight_matches')
      .update({
        archetype_id: newArchetype.id,
        anchor_text: anchorData,
        why_it_matters: whyData,
        is_auto_generated: true,
        updated_at: new Date().toISOString(),
      })
      .eq('song_id', songId)
      .eq('display_order', displayOrder)
      .select(
        `
        id,
        song_id,
        archetype_id,
        display_order,
        anchor_text,
        why_it_matters,
        archetype:insight_archetypes (
          id,
          insight_text,
          category,
          theme_tags
        )
      `
      )
      .single();

    if (updateError || !updated) {
      console.error('[rerollSingleInsight] Update error:', updateError);
      return null;
    }

    return updated as SongInsightMatch;
  } catch (err) {
    console.error('[rerollSingleInsight] Unexpected error:', err);
    return null;
  }
}
